import React, { useReducer, useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import NumberFormat from "react-number-format";

import { InputDispState, InputDispAction } from "./Input";
import { validate } from "../../util/validators";

export interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  value: string;
}

function NumberFormatCustom(props: NumberFormatCustomProps) {
  const { inputRef, onChange, name, value, ...other } = props;
  const [format, setFormat] = useState<string>("(##) ####-#####");

  useEffect(() => {
    const numValue = value?.replace(/\D/g, "") || "";

    if (numValue.length <= 10) {
      setFormat("(##) ####-#######");
    } else if (numValue.length === 11) {
      setFormat("(##) #####-#######");
    } else if (numValue.length > 11) {
      setFormat("+## (##) #####-####");
    }
  }, [value]);

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      value={value?.replace(/\D/g, "")}
      onValueChange={(values) => {
        onChange({
          target: {
            name: name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      decimalScale={0}
      format={format}
    />
  );
}

const inputReducer = (state: InputDispState, action: InputDispAction) => {
  switch (action.type) {
    case "CHANGE":
      return {
        ...state,
        value: /^[0-9]*$/.test(action.val)
          ? action.val
          : action.val.replace(/\D/g, ""),
        isValid: validate(
          /^[0-9]*$/.test(action.val)
            ? action.val
            : action.val.replace(/\D/g, ""),
          action.validators
        ),
      };
    case "TOUCH":
      return {
        ...state,
        isTouched: true,
      };
    case "UNTOUCH":
      return {
        ...state,
        isTouched: false,
      };
    default:
      return state;
  }
};

export interface InputPhoneProps {
  id: string;
  label: string;
  onInput: (id: string, value: string, isValid: boolean, label: string) => void;
  helperText: string;
  type: string;
  initialValue?: string;
  initialValid?: boolean;
  variant?: "standard" | "filled" | "outlined";
  forceError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  reset?: boolean;
  reinitialize?: boolean;
  changeValueTo?: string;
  validators: { type: string; val?: number }[];
}

export const InputPhone = (props: InputPhoneProps) => {
  const {
    id,
    label,
    onInput,
    forceError,
    helperText,
    type,
    initialValue,
    initialValid,
    variant,
    placeholder,
    disabled,
    validators,
    reinitialize,
    changeValueTo,
    reset,
  } = props;
  const [inputState, dispatch] = useReducer(inputReducer, {
    value: initialValue || "",
    isTouched: false,
    isValid: initialValid || false,
  });
  const { value, isValid, isTouched } = inputState;
  const [lastForcedValue, setLastForcedValue] = useState<string>();

  useEffect(() => {
    onInput(id, value, isValid, label.charAt(0).toUpperCase() + label.slice(1));
  }, [id, value, isValid, label, onInput]);

  useEffect(() => {
    if (changeValueTo && changeValueTo !== lastForcedValue) {
      setLastForcedValue(changeValueTo);

      dispatch({
        type: "CHANGE",
        val: changeValueTo,
        validators: validators,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeValueTo]);

  useEffect(() => {
    if (reset) {
      dispatch({
        type: "CHANGE",
        val: "",
        validators: validators,
      });
      dispatch({
        type: "UNTOUCH",
      });
    }
    if (reinitialize) {
      dispatch({
        type: "CHANGE",
        val: initialValue,
        validators: validators,
      });
      dispatch({
        type: "UNTOUCH",
      });
    }
  }, [reset, reinitialize, initialValue]);

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: "CHANGE",
      val: event.target.value,
      validators: validators,
    });
  };

  const touchHandler = () => {
    dispatch({ type: "TOUCH" });
  };

  return (
    <TextField
      disabled={disabled}
      error={(!isValid && isTouched) || forceError}
      id={id}
      name={id}
      value={value}
      variant={variant}
      onChange={changeHandler}
      onBlur={touchHandler}
      type={type}
      label={label.charAt(0).toUpperCase() + label.slice(1)}
      helperText={forceError ? helperText : !isValid && isTouched && helperText}
      placeholder={placeholder}
      InputProps={{
        inputComponent: NumberFormatCustom as any,
      }}
    />
  );
};
